import { PropType, defineComponent } from "vue";
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import * as generate from "nanoid/generate";
import { Flow } from "@mv/flow-shared-utils/types/flow";
import { loadDefinitionHistory, loadDefinitionDiagram, changeCategory, deployDefinition } from "@/store/flow/actions";

type CloseAction = () => void;

export default defineComponent({
  name: "sdk-flow-definition-history",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    definition: {
      type: Object as PropType<Flow.Process.Definition.definition>,
      required: true,
    },
    closeAction: {
      type: Function as PropType<CloseAction>,
      required: true,
    },
  },
  data() {
    return {
      definitionHistory: [] as any[],
      canvasBpmnEditorUid: `_cbpmn_uid_${generate("1234567890abcdef", 10)}`,
      bpmnViewer: {} as any,
      selectedDefinition: "",
      isRestoring: false,
      isFetchingDefinitions: false,
    };
  },
  computed: {
    restoreIsBlocked(): boolean {
      return !this.selectedDefinition || this.isRestoring;
    },
    sortedHistory(): any[] {
      return this.definitionHistory.map((d) => d).sort((a: any, b: any) => b.version - a.version);
    },
  },
  methods: {
    async loadDefinitionHistory() {
      this.isFetchingDefinitions = true;
      await this.$dispatch(loadDefinitionHistory)([this.definition.key])
        .then((res: any[]) => {
          this.definitionHistory = res;
        })
        .finally(() => {
          this.isFetchingDefinitions = false;
        });
    },
    clearState() {
      this.definitionHistory = [];
      this.selectedDefinition = "";
      this.isRestoring = false;
      if (Object.keys(this.bpmnViewer).length) {
        this.bpmnViewer.destroy();
        this.bpmnViewer = {};
      }
    },
    async showProcessDefinition(id: string) {
      this.selectedDefinition = id;

      if (!Object.keys(this.bpmnViewer).length) {
        this.bpmnViewer = new BpmnViewer({
          container: `#${this.canvasBpmnEditorUid}`,
        });
      }

      const { bpmn20Xml } = await this.$dispatch(loadDefinitionDiagram)([{ id }]);
      this.importXML(bpmn20Xml);
    },
    importXML(xmlDiagram: string) {
      this.bpmnViewer.importXML(xmlDiagram, (err: any) => {
        if (!err) {
          const canvas = this.bpmnViewer.get("canvas");
          canvas.zoom("fit-viewport");
        }
      });
    },
    incrementVersionTag(xml: string) {
      const latestVersion = Number(this.$route.params.definitionId.split(":")[1]) + 1;

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, "text/xml");
      xmlDoc.getElementsByTagName("bpmn:process")[0].setAttribute("camunda:versionTag", latestVersion.toString());
      const newXML = new XMLSerializer().serializeToString(xmlDoc);
      return newXML;
    },
    restoreDefinitionVersion() {
      this.isRestoring = true;
      this.bpmnViewer.saveXML({ format: true }, async (err: any, xml: any) => {
        if (!err) {
          const formattedXML = this.incrementVersionTag(xml);
          const processId = (formattedXML.match(/<bpmn:process id="(.+?)" name/) as RegExpMatchArray)[1];
          await this.$dispatch(deployDefinition)([formattedXML, processId], this.$t("flow.definitionBuilder.notifications.success"))
            .then(async (createdDefinition: any) => {
              const definitionId = Object.keys(createdDefinition.deployedProcessDefinitions)[0];
              const key = createdDefinition.deployedProcessDefinitions[definitionId].key;
              await this.$dispatch(changeCategory)([this.$route.params.categoryId, key]);
              this.$router.push({ name: "flow-definition", params: { categoryId: this.$route.params.categoryId, definitionKey: key, definitionId } });
              this.closeAction();
              window.location.reload();
            })
            .finally(() => {
              this.isRestoring = false;
            });
        }
      });
    },
  },
});
